import { Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import {
  HomePage,
  StorePage,
  ResourceLibraryPage,
  AdditionalPlaybookPage,
  AdditionalPlaybookReaderPage,
  ResourcePlaybookPage,
  ResourcePlaybookReaderPage,
  AcceleratorPage,
  HorseSensePage,
  ClinicsPage,
  PrivateSessionsPage,
  OnlineCoachingPage,
  OnlineCoachingBookedPage,
  OnlineCoachingManagePage,
  ProgramsPage,
  ProgramDetailPage,
  AboutPage,
  ContactPage,
  SupportPage,
  LoginPage,
  PortalPage,
  ProgramPlayerPage,
  AcceleratorCommunityPage,
  HorseSenseCommunityPage,
  ChatPage,
  PortalSettingsPage,
  ThankYouPage,
  PrivacyPolicyPage,
  TermsPage,
  RefundPolicyPage,
  ConfidentEquestrianPage,
  RidersFoundationGuidePage,
  GroundworkFoundationGuidePage,
  LibertyFoundationsPage,
  WelcomePage,
  AuthConfirmPage,
  AuthActionPage,
  SchedulePage,
  StartPage,
  AdminPage,
  FitnessProgramPage,
  TourPage,
  TourBarnsPage,
  TourBarnPage,
  WaiverPage,
  TextUpdatesPage,
} from './pages'
import { ProtectedRoute } from '../components/ProtectedRoute'
import { AdminRoute } from '../components/AdminRoute'
import { CoachScottWidget } from '../components/chat/CoachScottWidget'
import { RouteAnalytics } from '../components/RouteAnalytics'
import { AnalyticsConsent } from '../components/AnalyticsConsent'

const PageLoader = () => (
  <div className="min-h-screen bg-navy flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
  </div>
)

function ScrollToPageTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [pathname, hash])

  return null
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToPageTop />
      <RouteAnalytics />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/resources/:slug" element={<AdditionalPlaybookPage />} />
          <Route path="/resources/:slug/read" element={<AdditionalPlaybookReaderPage />} />
          <Route path="/resources" element={<ResourceLibraryPage />} />
          <Route path="/resources/riders-foundation" element={<ResourcePlaybookPage />} />
          <Route path="/resources/riders-foundation/read" element={<ResourcePlaybookReaderPage />} />
          <Route path="/start" element={<StartPage />} />
          <Route path="/accelerator" element={<AcceleratorPage />} />
          <Route path="/horse-sense" element={<HorseSensePage />} />
          <Route path="/waiver/:eventId/:attendeeId" element={<WaiverPage />} />
          <Route path="/clinics" element={<Navigate to="/masterclass" replace />} />
          <Route path="/masterclass" element={<ClinicsPage />} />
          <Route path="/private-sessions" element={<PrivateSessionsPage />} />
          <Route path="/online-coaching" element={<OnlineCoachingPage />} />
          <Route path="/online-coaching/booked" element={<OnlineCoachingBookedPage />} />
          <Route path="/online-coaching/manage" element={<OnlineCoachingManagePage />} />
          <Route path="/tour" element={<TourPage />} />
          <Route path="/tour/barns" element={<TourBarnsPage />} />
          <Route path="/tour/barn/:slug" element={<TourBarnPage />} />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route path="/programs/confident-equestrian" element={<ConfidentEquestrianPage />} />
          <Route path="/programs/riders-foundation-guide" element={<RidersFoundationGuidePage />} />
          <Route path="/programs/groundwork-foundation-guide" element={<GroundworkFoundationGuidePage />} />
          <Route path="/programs/liberty-foundations" element={<LibertyFoundationsPage />} />
          <Route path="/programs/:programId" element={<ProgramDetailPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/text-updates" element={<TextUpdatesPage />} />
          <Route path="/sms" element={<Navigate to="/text-updates" replace />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/welcome" element={<WelcomePage />} />
          {/* TODO: Remove /auth/confirm route 24 hours after the final deploy
              of the old generateSignInWithEmailLink code. Kept live so any
              pre-change magic links still in inboxes continue to land on a
              real page until they expire. */}
          <Route path="/auth/confirm" element={<AuthConfirmPage />} />
          <Route path="/auth/action" element={<AuthActionPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/portal" element={<ProtectedRoute><PortalPage /></ProtectedRoute>} />
          <Route path="/portal/programs/fitness-for-equestrians" element={<ProtectedRoute><FitnessProgramPage /></ProtectedRoute>} />
          <Route path="/portal/programs/:programId" element={<ProtectedRoute><ProgramPlayerPage /></ProtectedRoute>} />
          <Route path="/portal/chat/:programId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/portal/settings" element={<ProtectedRoute><PortalSettingsPage /></ProtectedRoute>} />
          <Route path="/portal/community/accelerator" element={<ProtectedRoute><AcceleratorCommunityPage /></ProtectedRoute>} />
          <Route path="/portal/community/horse-sense" element={<ProtectedRoute><HorseSenseCommunityPage /></ProtectedRoute>} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          {/* Inactive routes -redirect to home */}
          <Route path="/horse-or-home" element={<Navigate to="/" replace />} />
          <Route path="/podcast" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CoachScottWidget />
      <AnalyticsConsent />
    </BrowserRouter>
  )
}

